import { paintedPlaceMaki } from '@/features/locations/utils/makiPaths';
import { LANDOWNER_TYPES, type INearbyPlace, type LandownerType } from '@/types/api';

export { LANDOWNER_TYPES };
export type { LandownerType };

const FROM_ICON: Record<string, LandownerType> = {
  airport: 'Airport',
  fuel: 'Gas station',
  lodging: 'Hotel',
  'fast-food': 'Fast food restaurant',
  'parking-paid': 'Parking (with payment)',
};

/** Readable kinds that name one of the five, when the place carries no icon of its own. */
const FROM_READABLE_TYPE: Record<string, LandownerType> = {
  Airport: 'Airport',
  Fuel: 'Gas station',
  Hotel: 'Hotel',
  'Fast Food': 'Fast food restaurant',
};

function fromIcon(icon: string): LandownerType | '' {
  return FROM_ICON[icon] ?? '';
}

/** A select value is a category, or empty when it is the placeholder. */
export function asLandownerType(value: string): LandownerType | '' {
  return (LANDOWNER_TYPES as readonly string[]).includes(value) ? (value as LandownerType) : '';
}

/** The icon a picked place is stored under. A place with no icon keeps its readable kind. */
export function linkedPlaceType(place: Pick<INearbyPlace, 'type' | 'maki' | 'maki_beta'>): string {
  const icon = paintedPlaceMaki(place.maki ?? '', place.maki_beta ?? '');
  return icon || place.type;
}

/**
 * The category a stored place decides. Its type is the icon the map painted. Anything else,
 * including a supermarket or a free car park, stays blank.
 */
export function landownerTypeFromStored(type: string | undefined): LandownerType | '' {
  if (!type) return '';
  return fromIcon(type) || FROM_READABLE_TYPE[type] || '';
}

/**
 * The category a picked place decides. The newer icon wins, so paid parking is not read as a
 * free car park. A kind the icon does not decide stays blank.
 */
export function landownerTypeFromPlace(
  place: Pick<INearbyPlace, 'type' | 'maki' | 'maki_beta'>,
): LandownerType | '' {
  const fromBeta = place.maki_beta ? fromIcon(place.maki_beta) : '';
  if (fromBeta) return fromBeta;
  const fromMaki = place.maki ? fromIcon(place.maki) : '';
  if (fromMaki) return fromMaki;
  return FROM_READABLE_TYPE[place.type] ?? '';
}
